import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PURGE } from "redux-persist";
import apiInstance from '../../../../api';
import { removeProductModeration, updateProducts } from '../kitchenReducers/kitchenProductsReducer';
import ym from 'react-yandex-metrika';

export const getModerationProducts = createAsyncThunk(
    'moderationProducts/getModerationProducts',
    async (data, { rejectWithValue }) => {
        const api = '/moderationsQueue/get?XDEBUG_SESSION_START=MyVSCode';
    
        try {
            const response = await apiInstance.post(api, JSON.stringify({ data }));
    
            if (response.data.result) {
                return {
                    products: response.data.data.items,
                    total: response.data.data.total,
                };
            } else {
                return rejectWithValue('Failed to fetch data');
            }
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

async function blobUrlToFile(blobUrl, fileName) {
    // 1. Загружаем blob-URL как Blob
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    
    // 2. Преобразуем Blob в File
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
}

export const addProductForModeration = createAsyncThunk(
    'moderationProducts/addProductForModeration',
    async (data, { rejectWithValue, dispatch }) => {
        const api = '/moderationsQueue/create?XDEBUG_SESSION_START=MyVSCode';
    
        let formData = new FormData();

        // Если data.image - это blob-URL (строка)
        if (typeof data.image === 'string' && data.image.startsWith('blob:')) {
            try {
                const file = await blobUrlToFile(data.image, 'product-image.jpg');
                formData.append('image', file);
            } catch (error) {
                return rejectWithValue('Failed to convert blob URL to file');
            }
        } else if (data.image instanceof Blob) {
            // Если это Blob/File
            formData.append('image', data.image);
        }

        formData.append('data', JSON.stringify(data));

        try {
            const response = await apiInstance.post(api, formData);
    
            if (response.data.result) {
                dispatch(updateProducts({
                    ...data,
                    id: response.data.data.productId,
                    status: data.status || 'new',
                    fiscalType: 'product',
                    moderation: {
                        id: response.data.data.moderationId,
                        comment: "На модерации",
                        status: "pending",
                        productData: data,
                    }
                }));
                ym('reachGoal','product_add_to_moderation');
                return response.data.data;
            } else {
                return rejectWithValue('Failed to fetch data');
            }
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

export const removeProductFromModeration = createAsyncThunk(
    'moderationProducts/removeProductFromModeration',
    async (data, { rejectWithValue, dispatch }) => {
        const api = '/moderationsQueue/deleteModeration?XDEBUG_SESSION_START=MyVSCode';

        try {
            const response = await apiInstance.post(api, JSON.stringify({ data }));
    
            if (response.data.result) {
                dispatch(removeProductModeration(data.moderationId));
                ym('reachGoal','product_remove_from_moderation');
                return response.data.data;
            } else {
                return rejectWithValue('Failed to fetch data');
            }
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

export const applyModerationProduct = createAsyncThunk(
    'moderationProducts/applyModerationProduct',
    async (data, { rejectWithValue }) => {
        const api = '/moderationsQueue/approve?XDEBUG_SESSION_START=MyVSCode';
    
        try {
            const response = await apiInstance.post(api, JSON.stringify({ data }));
    
            if (response.data.result) {
                ym('reachGoal','product_apply_moderation');
                return data;
            } else {
                return rejectWithValue('Failed to fetch data');
            }
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

export const rejectModerationProduct = createAsyncThunk(
    'moderationProducts/rejectModerationProduct',
    async (data, { rejectWithValue }) => {
        const api = '/moderationsQueue/reject?XDEBUG_SESSION_START=MyVSCode';
    
        try {
            const response = await apiInstance.post(api, JSON.stringify({ data }));
    
            if (response.data.result) {
                ym('reachGoal','product_reject_moderation');
                return data;
            } else {
                return rejectWithValue('Failed to fetch data');
            }
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

const initialState = {
    getModerationProductsStatus: null,
    addProductForModerationStatus: null,
    removeProductFromModerationStatus: null,
    applyModerationProductStatus: null,
    rejectModerationProductStatus: null,

    moderationProducts: [],
    totalModerationProducts: 0,
    currentPage: 1,

    previewImage: null,
};

const moderationProductsSlice = createSlice({
    name: 'moderationProducts',
    initialState,
    extraReducers: (builder) => {
        builder
            // Fetch Data
            .addCase(getModerationProducts.pending, (state) => {
                state.getModerationProductsStatus = 'pending';
            })
            .addCase(getModerationProducts.fulfilled, (state, action) => {
                state.getModerationProductsStatus = 'fulfilled';
                const { products, total } = action.payload;
                state.moderationProducts = products;
                state.totalModerationProducts = total;
            })            
            .addCase(getModerationProducts.rejected, (state) => {
                state.getModerationProductsStatus = 'rejected';
            })
            .addCase(addProductForModeration.pending, (state) => {
                state.addProductForModerationStatus = 'pending';
            })
            .addCase(addProductForModeration.fulfilled, (state) => {
                state.addProductForModerationStatus = 'fulfilled';
            })            
            .addCase(addProductForModeration.rejected, (state) => {
                state.addProductForModerationStatus = 'rejected';
            })
            .addCase(removeProductFromModeration.pending, (state) => {
                state.removeProductFromModerationStatus = 'pending';
            })
            .addCase(removeProductFromModeration.fulfilled, (state, action) => {
                state.removeProductFromModerationStatus = 'fulfilled';
                // Попробуем удалить из текущей страницы, если есть moderationId
                try {
                    const moderationId = action?.meta?.arg?.moderationId || action?.payload?.moderationId;
                    if (moderationId) {
                        state.moderationProducts = state.moderationProducts.filter(product => product.moderationId !== moderationId);
                    }
                } catch (e) {
                    // безопасно игнорируем
                }
                // Поскольку используется пагинация, корректнее просто скорректировать общий тотал на -1
                state.totalModerationProducts = Math.max(0, (state.totalModerationProducts || 0) - 1);
            })
            .addCase(removeProductFromModeration.rejected, (state) => {
                state.removeProductFromModerationStatus = 'rejected';
            })
            .addCase(applyModerationProduct.pending, (state) => {
                state.applyModerationProductStatus = 'pending';
            })
            .addCase(applyModerationProduct.fulfilled, (state, action) => {
                state.applyModerationProductStatus = 'fulfilled';
                try {
                    const moderationId = action?.payload?.moderationId || action?.meta?.arg?.moderationId;
                    if (moderationId) {
                        state.moderationProducts = state.moderationProducts.filter(product => product.moderationId !== moderationId);
                    }
                } catch (e) {
                    // ignore
                }
                state.totalModerationProducts = Math.max(0, (state.totalModerationProducts || 0) - 1);
            })
            .addCase(applyModerationProduct.rejected, (state) => {
                state.applyModerationProductStatus = 'rejected';
            })
            .addCase(rejectModerationProduct.pending, (state) => {
                state.rejectModerationProductStatus = 'pending';
            })
            .addCase(rejectModerationProduct.fulfilled, (state, action) => {
                state.rejectModerationProductStatus = 'fulfilled';
                try {
                    const moderationId = action?.payload?.moderationId || action?.meta?.arg?.moderationId;
                    if (moderationId) {
                        state.moderationProducts = state.moderationProducts.filter(product => product.moderationId !== moderationId);
                    }
                } catch (e) {
                    // ignore
                }
                state.totalModerationProducts = Math.max(0, (state.totalModerationProducts || 0) - 1);
            })
            .addCase(rejectModerationProduct.rejected, (state) => {
                state.rejectModerationProductStatus = 'rejected';
            })
            // PURGE
            .addCase(PURGE, () => {
                return initialState;
            });
    },
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        setPreviewImage: (state, action) => {
            state.previewImage = action.payload;
        },
    },
});

export const {
    setCurrentPage,
    setPreviewImage,
} = moderationProductsSlice.actions;

export default moderationProductsSlice.reducer;
